import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useEffect, useMemo, useState } from 'react'
import ECharts from '~/components/echarts'
import { awsEC2SbuUtilizationMonthlyChartPath, dashboardOrganizationAWSEC2SbuUtilizationIndexPath } from '~/routes'
import { getJSON } from '~/utils/get_json'

function formatPercentage(value) {
  if (value == null) return '0%'
  return `${Number(value).toFixed(2)}%`
}

function getUtilizationStatus(utilization) {
  if (utilization == null || utilization === '' || utilization === 0.0) return {}
  const num = Number(utilization)
  if (num < 10) {
    return { label: t`Underutilized`, color: 'warning' }
  } else if (num < 20) {
    return { label: t`Normal`, color: 'info' }
  } else {
    return { label: t`Well utilized`, color: 'success' }
  }
}

export default function AWSEC2SbuUtilizationTab({ organizationId: orgId }) {
  const [summaryData, setSummaryData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const monthlyChartUrl = useMemo(() => awsEC2SbuUtilizationMonthlyChartPath(orgId), [orgId])

  useEffect(() => {
    const url = dashboardOrganizationAWSEC2SbuUtilizationIndexPath(orgId)
    const { request } = getJSON(url)

    request
      .then((jsonData) => {
        setSummaryData({
          overall_utilization: jsonData.overall_utilization,
          utilization_summary_last_month: jsonData.utilization_summary_last_month,
        })
        setLoading(false)
      })
      .catch((err) => {
        console.log(`error in getting SBU utilization data. Error: ${err}`)
        setLoading(false)
        setError(t`Error loading data. Backend dashboard endpoint may need to be created.`)
      })
  }, [orgId])

  if (loading && !summaryData) {
    return (
      <div className='text-center p-4'>
        <Trans>Loading...</Trans>
      </div>
    )
  }

  if (error) {
    return (
      <div className='alert alert-danger'>
        <Trans>Error loading data. Backend dashboard endpoint may need to be created.</Trans>
      </div>
    )
  }

  if (!summaryData) {
    return null
  }

  const overallUtilization = summaryData.overall_utilization
  const utilizationSummary = summaryData.utilization_summary_last_month

  return (
    <div className='row gy-4'>
      {/* Overall Utilization Summary */}
      <div className='col-md-6'>
        <div className='card'>
          <div className='card-body'>
            <h5 className='card-title'>
              <Trans>Overall SBU Utilization Summary</Trans>
            </h5>
            <p className='text-muted small mb-2'>
              <Trans>
                SBU utilization shows the percentile-based utilization metrics (p90, p95, p98, p99) for EC2 instances.
              </Trans>
            </p>
            <div>
              <p className='mb-1'>
                <strong>
                  <Trans>P90</Trans>:
                </strong>{' '}
                {formatPercentage(overallUtilization.p90)}
              </p>
              <p className='mb-1'>
                <strong>
                  <Trans>P95</Trans>:
                </strong>{' '}
                {formatPercentage(overallUtilization.p95)}
              </p>
              <p className='mb-1'>
                <strong>
                  <Trans>P98</Trans>:
                </strong>{' '}
                {formatPercentage(overallUtilization.p98)}
              </p>
              <p className='mb-2'>
                <strong>
                  <Trans>P99</Trans>:
                </strong>{' '}
                {formatPercentage(overallUtilization.p99)}
              </p>
              {getUtilizationStatus(overallUtilization.p99).label && (
                <p className='text-muted small mb-0'>
                  <Trans>Status</Trans>:&nbsp;
                  <span className={`text-${getUtilizationStatus(overallUtilization.p99).color}`}>
                    {getUtilizationStatus(overallUtilization.p99).label}
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Utilization Summary last month */}
      <div className='col-md-6'>
        <div className='card'>
          <div className='card-body'>
            <h5 className='card-title'>
              <Trans>Utilization Summary (Last Month)</Trans>
            </h5>
            <div className='mb-3'>
              <p className='mb-1'>
                <strong>
                  <Trans>P90</Trans>:
                </strong>{' '}
                {formatPercentage(utilizationSummary.p90)}
              </p>
              <p className='mb-1'>
                <strong>
                  <Trans>P95</Trans>:
                </strong>{' '}
                {formatPercentage(utilizationSummary.p95)}
              </p>
              <p className='mb-1'>
                <strong>
                  <Trans>P98</Trans>:
                </strong>{' '}
                {formatPercentage(utilizationSummary.p98)}
              </p>
              <p className='mb-2'>
                <strong>
                  <Trans>P99</Trans>:
                </strong>{' '}
                {formatPercentage(utilizationSummary.p99)}
              </p>
              {getUtilizationStatus(utilizationSummary.p99).label && (
                <p className='text-muted small mb-0'>
                  <Trans>Status</Trans>:&nbsp;
                  <span className={`text-${getUtilizationStatus(utilizationSummary.p99).color}`}>
                    {getUtilizationStatus(utilizationSummary.p99).label}
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Monthly SBU Utilization Chart */}
      <div className='col-md-6'>
        <div className='card'>
          <div className='card-body'>
            <ECharts
              title={t`Monthly SBU Utilization`}
              chartInfoTitle='aws_ec2_sbu_utilization_monthly'
              url={monthlyChartUrl}
              params={{}}
              showExportButton={false}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
